import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  getFCMToken, 
  deleteFCMToken, 
  onMessageReceived,
  requestNotificationPermission,
  getMessagingInstance
} from '../api/messaging';
import { checkFCMSupport } from '../config/fcm';
import { MessagePayload } from 'firebase/messaging';

export const useFirebaseMessaging = () => {
  const { firestoreUser } = useAuth();
  const [fcmToken, setFcmToken] = useState<string | null>(null);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [isSupported, setIsSupported] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  // Check if notifications are supported
  useEffect(() => {
    const checkSupport = async () => {
      try {
        const supportResult = await checkFCMSupport();
        setIsSupported(supportResult.isSupported);
        
        if (supportResult.isSupported) {
          setPermission(Notification.permission);
        }
      } catch (error) {
        console.error('Error checking FCM support:', error);
        setIsSupported(false);
      }
    };
    
    checkSupport();
  }, []);

  // Request notification permission
  const requestPermission = useCallback(async () => {
    if (!isSupported) {
      console.warn('Notifications not supported in this environment');
      return false;
    }

    try {
      const granted = await requestNotificationPermission();
      setPermission(granted ? 'granted' : 'denied');
      return granted;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }, [isSupported]);

  // Get FCM token
  const getToken = useCallback(async () => {
    if (!isSupported || permission !== 'granted') {
      return null;
    }

    try {
      // Get current user ID from context
      const currentUserId = firestoreUser?.id;
      if (!currentUserId) {
        console.warn('⚠️ No user ID available, cannot save FCM token to device collection');
        return null;
      }

      const token = await getFCMToken(currentUserId);
      setFcmToken(token);
      return token;
    } catch (error) {
      console.error('Error getting FCM token:', error);
      return null;
    }
  }, [isSupported, permission, firestoreUser?.id]);

  // Delete FCM token
  const removeToken = useCallback(async () => {
    try {
      await deleteFCMToken();
      setFcmToken(null);
    } catch (error) {
      console.error('Error deleting FCM token:', error);
    }
  }, []);

  // Setup message listener
  const setupMessageListener = useCallback((callback: (payload: MessagePayload) => void) => {
    if (!isSupported) return;

    const unsubscribe = onMessageReceived(callback);
    return unsubscribe;
  }, [isSupported]);

  // Initialize messaging when user is authenticated
  useEffect(() => {
    const initializeMessaging = async () => {
      if (firestoreUser && isSupported && permission === 'granted' && !isInitialized) {
        try {
          const messagingInstance = await getMessagingInstance();
          if (messagingInstance) {
            const token = await getToken();
            if (token) {
              setIsInitialized(true);
            }
          }
        } catch (error) {
          console.error('Error initializing FCM:', error);
        }
      }
    };

    initializeMessaging();
  }, [firestoreUser, isSupported, permission, isInitialized, getToken]);

  // Cleanup token when user logs out
  useEffect(() => {
    if (!firestoreUser && fcmToken) {
      removeToken();
      setIsInitialized(false);
    }
  }, [firestoreUser, fcmToken, removeToken]);

  // Auto-request permission when supported
  useEffect(() => {
    if (isSupported && permission === 'default' && firestoreUser) {
      requestPermission();
    }
  }, [isSupported, permission, firestoreUser, requestPermission]);

  return {
    fcmToken,
    permission,
    isSupported,
    isInitialized,
    requestPermission,
    getToken,
    removeToken,
    setupMessageListener
  };
};
